import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useFlags } from 'helpers/useFlags';
import { useFeatureAccess } from 'helpers/serviceTierHooks';
import { activeUserHasPermission } from 'services/user/redux';
import { useSnackbar } from 'notistack';

import { SecureRouteProps } from './types';

const SecureRoute: React.FC<SecureRouteProps> = (props) => {
  const {
    permissions = [],
    redirectToHome = false,
    featureFlag,
    requiredFeature,
  } = props;

  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const canAccess = useSelector(activeUserHasPermission(permissions));
  const flags = useFlags();
  const featureAccess = useFeatureAccess();

  // Check if the feature flag is enabled (if specified)
  const enabledFeatureFlag = featureFlag ? flags[featureFlag] : true;

  // Check if the required feature is accessible (if specified)
  const hasFeatureAccess = requiredFeature
    ? featureAccess[requiredFeature] === true
    : true;

  // Show error message and prevent navigation if access is denied
  if (
    !enabledFeatureFlag ||
    !canAccess ||
    !hasFeatureAccess ||
    redirectToHome
  ) {
    // Only show the message if we're not already on the home page
    if (location.pathname !== '/') {
      enqueueSnackbar('You do not have access to this feature', {
        variant: 'error',
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        autoHideDuration: 3000,
      });
    }

    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

export default SecureRoute;
